import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Control, DomEvent, DomUtil, Map } from 'leaflet';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Indicator } from '../../models/Indicator';
import {
    ConnectionCostParameters,
    NewRoof,
    BankLoanResult,
    Profile,
    ProfileEnum,
    Contact,
} from '../../models/Solar';

import { LocalStorageService } from '../local-storage.service';
import { MapService } from '../map.service';
import { ModuleService } from '../module.service';
import { RestService } from '../RestService';
import { TerService } from '../TerService';
import { UsefulService } from '../UsefulService';

import { CadastreSolaireInfoModalComponent } from '../../controllers/main/components/module-cadastre-solaire/modals/info/cadastre-solaire-info-modal.component';

@Injectable({
    providedIn: 'root',
})
export class SolarService {
    public lomiaCtaMobileControl: Control;

    public selectedElementsObs = new BehaviorSubject<any[]>([]);
    selectedElementsObs$: Observable<any[]> = this.selectedElementsObs.asObservable();

    public mainStageObs = new BehaviorSubject<string | null>(null);
    mainStageObs$: Observable<string | null> = this.mainStageObs.asObservable();

    public pvStageObs = new BehaviorSubject<string | null>(null);
    pvStageObs$: Observable<string | null> = this.pvStageObs.asObservable();

    public thermalStageObs = new BehaviorSubject<string | null>(null);
    thermalStageObs$: Observable<string | null> = this.thermalStageObs.asObservable();

    public newElementObs = new BehaviorSubject<any>(null);
    newElementObs$: Observable<any> = this.newElementObs.asObservable();

    public simulationBankLoanObs = new BehaviorSubject<boolean>(false);
    public simulationBankLoanObs$: Observable<boolean> = this.simulationBankLoanObs.asObservable();

    private bankLoanResultObs = new BehaviorSubject<BankLoanResult | null>(null);
    public bankLoanResultObs$ = this.bankLoanResultObs.asObservable();

    public address: GeoJSON.Feature;

    public customInfo: any = null;

    public selectionInfo: {
        showLoader: boolean;
        myRoof: string;
        totalModuleArea: string;
        totalGrossRoofArea: string;
        direction: string;
        tilt: string;
    };
    public selectedElements: any[] = [];
    public indicatorPlot: Indicator | null;
    public technology: string;
    public moduleInfo: any;
    public contactsInfo: Contact[];
    public totalGrossRoofArea: number;

    public isAnyZoneConstraint: boolean;
    public isConstraintZone = {
        architectural: true,
        nature: true,
        altitude: true,
        coastal: true,
    };

    public valorization: string = '';

    public connectionCostInfo: {
        isEnedisAvailable: boolean;
        contactRegie: string;
        aecEstimation: any;
    };

    public results: any[];

    private titleByStage = {
        newRoofStage: { title: 'Simulation de projet solaire', subTitle: null },
        initStage: { title: 'Simulation de projet solaire', subTitle: null },
        parametersStage: { title: 'Simulation de projet solaire', subTitle: null },
        profileStage: { title: 'Profil utilisateur', subTitle: null },
        technologyStage: { title: 'Choix de la technologie', subTitle: null },
        thermalStage: { title: 'Simulation de projet : thermique', subTitle: null },
        pvStage: { title: 'Simulation de projet : photovoltaïque', subTitle: null },
        pvValorizationStage: { title: 'Simulation de projet : photovoltaïque', subTitle: null },
        pvTotalityStage: {
            title: 'Simulation de projet : photovoltaïque',
            subTitle: 'Vente totale',
        },
        pvSurplusStage: {
            title: 'Simulation de projet : photovoltaïque',
            subTitle: 'Autoconsommation avec revente du surplus',
        },
        resultsStage: { title: 'Mes simulations de projet', subTitle: null },
    };

    public distributions: any[] = [];

    public warningMessageRoi: string = `Le retour sur investissement de votre projet dépasse 20 ans. Cela le rend peu rentable économiquement même s’il présente un réel intérêt écologique. En modifiant la taille de l’installation, vous pouvez améliorer sa rentabilité tout en conservant son impact positif sur l’environnement.`;
    public warningLimitRoi: number = 20;

    public profile: ProfileEnum | null = null;

    constructor(
        private modalService: NgbModal,
        @Inject(LocalStorageService) private localStorageService: LocalStorageService,
        @Inject(MapService) private mapService: MapService,
        @Inject(ModuleService) public moduleService: ModuleService,
        @Inject(RestService) private restService: RestService,
        @Inject(TerService) private terService: TerService,
        @Inject(UsefulService) private usefulService: UsefulService,
    ) {}

    clear() {
        this.customInfo = null;
        this._clearObservables();
    }

    private _clearObservables() {
        this.selectedElementsObs.next([]);
        this.newElementObs.next(null);
        this.mainStageObs.next('');
        this.pvStageObs.next('');
        this.thermalStageObs.next('');
        this.simulationBankLoanObs.next(false);
        this.bankLoanResultObs.next(null);
    }

    updateMainStage(stage: string | null) {
        if (this.indicatorPlot) {
            const isInitStage = [null, 'initStage', 'newRoofStage'].includes(stage);
            const isAnyResultDefined = this.results.length > 0;
            this.indicatorPlot.openPopup = isInitStage && !isAnyResultDefined;
        }
        this.mainStageObs.next(stage);
    }

    updatePvStage(stage: string) {
        this.pvStageObs.next(stage);
    }

    updateThermalStage(stage: string) {
        this.thermalStageObs.next(stage);
    }

    async initSelectedElements() {
        this.removeAllElements();
        this.moduleService.openModule('solar');
    }

    initProfile() {
        this.profile = null;
    }

    setProfile(profile: ProfileEnum) {
        this.profile = profile;
    }

    getProfile(): ProfileEnum | null {
        return this.profile;
    }

    initSelectionInfo() {
        this.selectionInfo = {
            showLoader: true,
            myRoof: '',
            totalModuleArea: '',
            totalGrossRoofArea: '',
            direction: '',
            tilt: '',
        };
    }

    initResults() {
        this.results = [];
    }

    initDistributions() {
        this.distributions = [];
    }

    addResult(result: any) {
        this.results.push(result);
    }

    removeResult(resultToRemove: any) {
        const index = this.results.indexOf(resultToRemove);
        this.results.splice(index, 1);
    }

    deselectAllElements() {
        this.selectedElements.forEach((element) => this.indicatorPlot.deselectElement(element.id));
    }

    removeElement(elementId: number | string) {
        this.indicatorPlot.deselectElement(elementId);
        this.selectedElements = this.selectedElements.filter(
            (element: any) => element.id != elementId,
        );
        this.checkZoneConstraint();
        this.selectedElementsObs.next(this.selectedElements);
    }

    removeAllElements() {
        this.deselectAllElements();
        this.selectedElements = [];
        this.checkZoneConstraint();
        this.selectedElementsObs.next(this.selectedElements);
    }

    keepOnlyFirstElement() {
        this.selectedElements
            .slice(1)
            .forEach((element) => this.indicatorPlot.deselectElement(element.id));
        this.selectedElements = this.selectedElements.slice(0, 1);
        this.selectedElementsObs.next(this.selectedElements);
    }

    async addSelectedElement(feature: GeoJSON.Feature) {
        this.selectionInfo.showLoader = true;
        const selectedElement = await this._getElementInfo(feature.properties);
        selectedElement.isNew = false;
        selectedElement.center = feature.properties.center;
        selectedElement.addressLabel = this.formatAddressLabel(feature);
        selectedElement.parcelLabel = this.formalParcelLabel(feature);
        this.setInfoByElement(selectedElement);

        this.addElement(selectedElement);
    }

    addElement(element: any) {
        this.selectedElements.push(element);
        this.checkZoneConstraint();
        this.selectedElementsObs.next(this.selectedElements);
    }

    private async _getElementInfo(element: any) {
        try {
            const parameters = {
                year: this.indicatorPlot.crrsdc_ter_year_geo,
                scaleTypeId: this.terService.territoryScale.typeId,
                territoryIds: JSON.stringify(this.terService.territories.map((t) => t.id)),
                indicatorId: element.id_indicateur,
                elementId: element.id,
            };

            const selectedElement = this.restService.getSolarElementInfo(parameters);
            return selectedElement;
        } catch (error) {
            console.error('Error _getElementInfo', error);
            throw error;
        }
    }

    async addNewElement(newElement: any, latlng: L.LatLng) {
        this.selectedElements.push(newElement);
        await this.checkConstraintZone(latlng);
        this.newElementObs.next(newElement);
    }

    removeNewElement() {
        this.newElementObs.next(null);
    }

    setNewElementParameters(newEement: NewRoof, parameters: Partial<NewRoof>) {
        const index = this.selectedElements.indexOf(newEement);
        this.selectedElements[index] = { ...newEement, ...parameters };

        this.connectionCostInfo = {
            isEnedisAvailable: false,
            contactRegie: '',
            aecEstimation: null,
        };
    }

    getPvParameters() {
        const parameters = {
            year: this.indicatorPlot!.crrsdc_ter_year_geo,
            scaleTypeId: this.terService.territoryScale.typeId,
            territoryIds: JSON.stringify(this.terService.territories.map((t) => t.id)),
        };
        return this.restService.getSolarPvParameters(parameters);
    }

    getSolarCustomTerritoryInfo() {
        const parameters = {
            year: this.indicatorPlot!.crrsdc_ter_year_geo,
            scaleTypeId: this.terService.territoryScale.typeId,
            territoryIds: JSON.stringify(this.terService.territories.map((t) => t.id)),
            profile: this.profile,
            technology: this.technology,
        };
        return this.restService.getSolarCustomTerritoryInfo(parameters);
    }

    saveSolarLogIn(label: string, latitude: number, longitude: number) {
        const user = this.localStorageService.get('user');
        const data = {
            userId: user.id,
            label: label,
            latitude: latitude,
            longitude: longitude,
        };
        this.restService.saveSolarLogIn(data);
    }

    isAlreadySelected(element: any) {
        return this.selectedElements.some(
            (selectedElement: any) => selectedElement.id == element.id,
        );
    }

    isDifferentTerritory() {
        let isSameYear = false;
        let isSameTypeTer = false;
        let isSameIdTer = false;

        if (!!this.customInfo) {
            isSameYear = this.customInfo.year == this.indicatorPlot.crrsdc_ter_year_geo;
            isSameTypeTer = this.customInfo.territoryType == this.terService.territoryScale.typeId;
            isSameIdTer =
                JSON.stringify(this.customInfo.territoryIds.sort()) ==
                JSON.stringify(this.terService.territories.map((t) => t.id).sort());
        }

        return [isSameYear, isSameTypeTer, isSameIdTer].includes(false);
    }

    setInfoByElement(element: any) {
        this.connectionCostInfo = element.connectionCostInfo;
        this._setUsableRoofAreaByElement(element);
        this._setRoofWindowAreaByElement(element);
        this._setRoofChimneyAreaByElement(element);
        this._setDirectionDetailByElement(element);
    }

    private _setUsableRoofAreaByElement(element: any = undefined) {
        if (element) {
            element.usableRoofArea = element.surface_disponible;
        } else {
            this.selectedElements.forEach(
                (element: any) => (element.usableRoofArea = element.surface_disponible),
            );
        }
    }

    private _setRoofWindowAreaByElement(element: any = undefined, value = 0) {
        if (element) {
            element.windowArea = value;
        } else {
            this.selectedElements.forEach((element: any) => {
                element.windowArea = value;
            });
        }
    }

    private _setRoofChimneyAreaByElement(element: any = undefined, value = 0) {
        if (element) {
            element.chimney = value;
        } else {
            this.selectedElements.forEach((element: any) => {
                element.chimney = value;
            });
        }
    }

    private _setDirectionDetailByElement(element: any = undefined) {
        if (element) {
            element.directionDetail = element.orientation_s;
        } else {
            this.selectedElements.forEach(
                (element: any) => (element.directionDetail = element.orientation_s),
            );
        }
    }

    setAddress(feature: GeoJSON.Feature) {
        this.address = feature;
        this.localStorageService.set('address', feature);
    }

    addMarker(geometry: GeoJSON.Point) {
        const lat = geometry.coordinates[1];
        const lng = geometry.coordinates[0];
        this.mapService.addMarkerAddress(lat, lng);
        this.mapService.centerOn(lat, lng, 18, false);
    }

    getAddress() {
        return this.address;
    }

    formatAddressLabel(feature: GeoJSON.Feature) {
        const address = feature.properties.address;
        const city = feature.properties.city;

        const addressItems = [];

        if (!!address && !['Mutiple', 'Multiple'].includes(address)) {
            addressItems.push(address);
        }

        if (!!city) {
            addressItems.push(city);
        }

        const addressLabel = addressItems.join(', ');
        return addressLabel;
    }

    formalParcelLabel(feature: GeoJSON.Feature) {
        const parcel = feature.properties.parcel;
        let parcelLabel = '';
        if (parcel) {
            parcelLabel = `N° de parcelle : ${parcel}`;
        }
        return parcelLabel;
    }

    formatOccupationLabel(feature: GeoJSON.Feature) {
        const occupation = feature.properties.occupation;
        const branch = feature.properties.branche;

        const occupationItems = [];
        if (occupation) {
            occupationItems.push(occupation);
        }
        if (!!branch && !['Mutiple', 'Multiple'].includes(branch)) {
            occupationItems.push(branch);
        }

        const occupationLabel = occupationItems.join(' / ');
        return occupationLabel;
    }

    getFirstGreater(obj: { [ppeak: string]: number }, value: number) {
        const keysSorted = Object.keys(obj)
            .map((k) => parseFloat(k))
            .sort((a, b) => a - b);

        const firstGreater = keysSorted.find((k) => k >= value);
        return firstGreater;
    }

    getLastSmaller(obj: { [ppeak: string]: number }, value: number) {
        const keysSorted = Object.keys(obj)
            .map((k) => parseFloat(k))
            .sort((a, b) => b - a);

        const firstSmaller = keysSorted.find((k) => k < value);

        // between 0 and first value, no interpolation needed so we used first value
        return firstSmaller || keysSorted[0];
    }

    getLinearInterpolatedValue(obj: { [ppeak: string]: number }, value: number) {
        const x1 = this.getLastSmaller(obj, value);
        const x2 = this.getFirstGreater(obj, value);

        if (x1 === undefined || x2 === undefined) {
            throw new Error('Cannot interpolate, values not found.');
        }

        const y1 = obj[x1] || 0;
        const y2 = obj[x2];
        const y = y1 + ((value - x1) * (y2 - y1)) / (x2 - x1);

        return y;
    }

    getTitleByStage(stage: string) {
        return this.titleByStage[stage].title;
    }

    getSubTitleByStage(stage: string) {
        return this.titleByStage[stage].subTitle;
    }

    setTotalGrossRoofArea() {
        this.totalGrossRoofArea = this.selectedElements.reduce(
            (sum, element) => sum + element.surface_disponible,
            0,
        );
    }

    checkZoneConstraint() {
        this.isConstraintZone.architectural = this.selectedElements.some(
            (element: any) => element.architectural_constraint,
        );
        this.isConstraintZone.altitude = this.selectedElements.some(
            (element: any) => element.altitude_constraint,
        );
        this.isConstraintZone.coastal = this.selectedElements.some(
            (element: any) => element.coastal_constraint,
        );
        this.isConstraintZone.nature = this.selectedElements.some(
            (element: any) => element.nature_constraint,
        );

        this.isAnyZoneConstraint = this.selectedElements.some(
            (element: any) =>
                element.architectural_constraint ||
                element.altitude_constraint ||
                element.coastal_constraint ||
                element.nature_constraint,
        );
    }

    async getConnectionCost(valorization: string, subscribedPower: number, totalPowerPeak: number) {
        const data: ConnectionCostParameters = {
            valorization: valorization,
            subscribedPower: subscribedPower,
            injectedPower: totalPowerPeak,
            elements: this.selectedElements,
            scaleTypeId: this.terService.territoryScale.typeId,
            territoryIds: this.terService.territories.map((t) => t.id),
            year: this.indicatorPlot.crrsdc_ter_year_geo,
        };

        const connectionCost = await this.restService.getConnectionCost(data);
        return connectionCost;
    }

    async getSolarPdfFile(result?: any) {
        const uniqueAddresses = this.selectedElements.reduce((acc, el) => {
            const item = { address: el.addressLabel, parcel: el.parcelLabel };
            const exists = acc.some(
                (i: any) => i.address === item.address && i.parcel === item.parcel,
            );
            return exists ? acc : [...acc, item];
        }, []);

        const data = {
            year: this.indicatorPlot.crrsdc_ter_year_geo,
            scaleTypeId: this.terService.territoryScale.typeId,
            territoryIds: this.terService.territories.map((t) => t.id),
            isConstraintZone: this.isConstraintZone,
            selectionInfo: this.selectionInfo,
            simulations: result ? [result] : this.results,
            addresses: uniqueAddresses,
            distributions: this.distributions,
            profile: this.profile,
        };

        const buffer = await this.restService.getSolarPdfFile(data);
        return buffer;
    }

    initCtaMobileControl() {
        const LomiaCtaMobileControl = Control.extend({
            onAdd(map: Map) {
                const element = DomUtil.get('lomia-cta-mobile-control');
                DomEvent.disableClickPropagation(element);
                return element;
            },
            onRemove(map: Map) {},
        });
        this.lomiaCtaMobileControl = new LomiaCtaMobileControl({
            position: 'bottomleft',
        });
    }

    addCtaMobileControlToMap() {
        this.lomiaCtaMobileControl.addTo(this.mapService.map);
    }

    removeCtaMobileControlFromMap() {
        this.mapService.map.removeControl(this.lomiaCtaMobileControl);
    }

    convertOrientation(orientation: string): number {
        let azimuth: number;
        switch (orientation) {
            case 'south':
                azimuth = 180;
                break;
            case 'west':
                azimuth = 270;
                break;
            case 'north':
                azimuth = 0;
                break;
            case 'est':
                azimuth = 90;
                break;
            case 'north-west':
                azimuth = 315;
                break;
            case 'north-est':
                azimuth = 45;
                break;
            case 'south-est':
                azimuth = 135;
                break;
            case 'south-west':
                azimuth = 225;
                break;
        }
        return azimuth;
    }

    checkIfSelectedElementsIsNew() {
        if (this.selectedElements.length > 1) {
            return false;
        }

        const selectedElement = this.selectedElements[0];
        const isNew = selectedElement.isNew;
        return isNew;
    }

    setTotalModuleAreaToSelectionInfo(totalModuleArea: number) {
        const stringifiedTotalModuleArea = this.usefulService.stringifyNumber(
            this.usefulService.round(totalModuleArea),
        );
        this.selectionInfo.totalModuleArea = stringifiedTotalModuleArea;
    }

    async checkConstraintZone(latlng: L.LatLng): Promise<void> {
        const parameters = {
            latitude: latlng.lat,
            longitude: latlng.lng,
        };
        this.isAnyZoneConstraint = await this.restService.checkConstraintZone(parameters);
    }

    setWarningRoi(roi: number): string {
        return roi > this.warningLimitRoi ? this.warningMessageRoi : '';
    }

    public getSolarCustomTerritoryControl(latitude: number, longitude: number): Promise<string> {
        const parameters = {
            year: this.indicatorPlot.crrsdc_ter_year_geo,
            scaleTypeId: this.terService.territoryScale.typeId,
            latitude,
            longitude,
        };
        return this.restService.getSolarCustomTerritoryControl(parameters);
    }

    public callSimulateBankLoan(
        loan: number,
        durationYear: number,
        annualInterestRate: number,
        initInvestment: number,
        gain: number,
    ) {
        const parameters = {
            loan: loan,
            durationYear: durationYear,
            annualInterestRate: annualInterestRate,
            initInvestment: initInvestment,
            gain: gain,
        };
        return this.restService.simulateBankLoan(parameters);
    }

    simulateBankLoan() {
        this.simulationBankLoanObs.next(true);
    }

    closeSimulateBankLoan() {
        this.simulationBankLoanObs.next(false);
    }

    setBankLoanResult(result: BankLoanResult) {
        this.bankLoanResultObs.next(result);
    }

    openBankLoanInfoModal() {
        const title = `Simulation d'emprunt bancaire`;
        const body = `Sélectionnez cette option pour accéder, à l'étape suivante, à l'outil d'estimation de l'emprunt bancaire et évaluer le coût du financement ainsi que son impact sur la rentabilité de votre projet.`;

        this.modalService.dismissAll();
        const modalRef = this.modalService.open(CadastreSolaireInfoModalComponent, { size: 'xs' });
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.body = body;
    }
}
